import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { EnvelopeSimple, Phone, MapPin, ArrowRight } from "@phosphor-icons/react";
import { SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", company: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. Our team will reach out within one business day.");
      setForm(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-[#FAFAFA] border-t border-slate-200">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <div>
          <span className="overline text-[#B91C1C]">Get in touch</span>
          <h2 className="mt-4 font-serif-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            Let&apos;s talk about your portfolio
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed max-w-md">
            Share your recovery challenge and our team will design a compliant engagement model tailored to your book.
          </p>

          <div className="mt-10 space-y-6">
            <a data-testid="contact-email" href="mailto:info@crestlinerisk.com" className="flex items-center gap-4 group">
              <span className="flex h-11 w-11 items-center justify-center border border-slate-300 group-hover:bg-slate-900 group-hover:text-white transition-colors duration-200">
                <EnvelopeSimple className="h-5 w-5" />
              </span>
              <div>
                <p className="overline text-slate-500">Email</p>
                <p className="text-slate-900 font-medium">info@crestlinerisk.com</p>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center border border-slate-300">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <p className="overline text-slate-500">Phone</p>
                <p className="text-slate-900 font-medium">+91 95825 39340 / +91 95571 24622</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center border border-slate-300">
                <MapPin className="h-5 w-5" />
              </span>
              <div>
                <p className="overline text-slate-500">Head Office</p>
                <p className="text-slate-900 font-medium">3rd Floor, C-18, Sector-2, Noida - 201301</p>
              </div>
            </div>
          </div>
        </div>

        <form data-testid="contact-form" onSubmit={submit} className="bg-white border border-slate-200 p-8 lg:p-10 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name" className="overline text-slate-500">Full name *</Label>
              <Input id="name" data-testid="contact-input-name" value={form.name} onChange={set("name")}
                className="mt-2 rounded-none border-slate-300 focus-visible:ring-slate-900" placeholder="Your name" />
            </div>
            <div>
              <Label htmlFor="email" className="overline text-slate-500">Email *</Label>
              <Input id="email" type="email" data-testid="contact-input-email" value={form.email} onChange={set("email")}
                className="mt-2 rounded-none border-slate-300 focus-visible:ring-slate-900" placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="phone" className="overline text-slate-500">Phone</Label>
              <Input id="phone" data-testid="contact-input-phone" value={form.phone} onChange={set("phone")}
                className="mt-2 rounded-none border-slate-300 focus-visible:ring-slate-900" placeholder="+91" />
            </div>
            <div>
              <Label htmlFor="company" className="overline text-slate-500">Organisation</Label>
              <Input id="company" data-testid="contact-input-company" value={form.company} onChange={set("company")}
                className="mt-2 rounded-none border-slate-300 focus-visible:ring-slate-900" placeholder="Bank / NBFC" />
            </div>
          </div>

          <div>
            <Label className="overline text-slate-500">Service of interest</Label>
            <Select value={form.service} onValueChange={set("service")}>
              <SelectTrigger data-testid="contact-select-service" className="mt-2 rounded-none border-slate-300 focus:ring-slate-900">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem key={s.key} value={s.title} data-testid={`service-option-${s.key}`}>{s.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="message" className="overline text-slate-500">Message *</Label>
            <Textarea id="message" data-testid="contact-input-message" value={form.message} onChange={set("message")}
              rows={4} className="mt-2 rounded-none border-slate-300 focus-visible:ring-slate-900 resize-none"
              placeholder="Tell us about your portfolio and recovery goals." />
          </div>

          <Button type="submit" data-testid="contact-submit-button" disabled={loading}
            className="w-full rounded-none bg-slate-900 hover:bg-[#B91C1C] text-white py-6 font-semibold transition-colors duration-200 group">
            {loading ? "Sending..." : (
              <span className="inline-flex items-center gap-2">Send Enquiry
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            )}
          </Button>
        </form>
      </div>
    </section>
  );
};
